/**
 * 원불교 법령 관리 시스템 - API 클라이언트
 * 
 * 이 파일은 Axios를 기반으로 한 HTTP 클라이언트를 설정합니다.
 * 인증 토큰 관리, 요청/응답 인터셉터, 에러 처리를 담당합니다.
 * 
 * 주요 기능:
 * - 자동 토큰 인증 헤더 추가
 * - 토큰 만료 시 자동 로그아웃
 * - 전역 에러 처리
 * 
 * @author 원불교 법령 관리 시스템 개발팀
 * @version 1.0.0
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios';

// API 기본 설정 - 환경변수에서 URL을 가져오거나 기본값 사용
const API_BASE_URL = process.env.REACT_APP_API_URL || `http://localhost:${process.env.REACT_APP_BACKEND_PORT || 3000}`;

// 개발 환경에서만 /api 프리픽스 추가 (운영환경에서는 이미 URL에 포함됨)
const isDevelopment = process.env.NODE_ENV === 'development';
const hasApiPrefix = API_BASE_URL.includes('/api');
const apiPrefix = isDevelopment && !hasApiPrefix ? '/api' : '';

// Axios 인스턴스 생성 - 기본 설정 적용
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}${apiPrefix}`, // API 서버 기본 URL
  timeout: 10000, // 요청 타임아웃 (10초)
  headers: {
    'Content-Type': 'application/json', // JSON 형식으로 통신
  },
});

// 메일 발송 전용 API 클라이언트 - 긴 타임아웃 설정
const emailApiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}${apiPrefix}`, // API 서버 기본 URL
  timeout: 60000, // 요청 타임아웃 (60초) - 메일 발송은 시간이 오래 걸릴 수 있음
  headers: {
    'Content-Type': 'application/json', // JSON 형식으로 통신
  },
});

// 요청 인터셉터 - 인증 토큰 자동 추가
apiClient.interceptors.request.use(
  (config) => {
    // 로컬 스토리지에서 액세스 토큰을 가져와서 요청 헤더에 추가
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 메일 발송 전용 요청 인터셉터
emailApiClient.interceptors.request.use(
  (config) => {
    // 로컬 스토리지에서 액세스 토큰을 가져와서 요청 헤더에 추가
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 전역 에러 처리 및 토큰 만료 처리
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // 401 Unauthorized 에러 처리 (토큰 만료 또는 유효하지 않은 토큰)
    if (error.response?.status === 401) {
      // 토큰이 만료되었거나 유효하지 않은 경우 로컬 스토리지 정리
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      
      // 로그인 페이지가 아닌 경우에만 리다이렉트
      if (!window.location.pathname.includes('/login')) {
        // React Router를 사용하여 프로그래매틱 네비게이션
        // window.location.href 대신 이벤트를 발생시켜 AuthContext에서 처리하도록 함
        window.dispatchEvent(new CustomEvent('auth:tokenExpired'));
      }
    }
    return Promise.reject(error);
  }
);

// 메일 발송 전용 응답 인터셉터
emailApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // 401 Unauthorized 에러 처리 (토큰 만료 또는 유효하지 않은 토큰)
    if (error.response?.status === 401) {
      // 토큰이 만료되었거나 유효하지 않은 경우 로컬 스토리지 정리
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      
      // 로그인 페이지가 아닌 경우에만 리다이렉트
      if (!window.location.pathname.includes('/login')) {
        window.dispatchEvent(new CustomEvent('auth:tokenExpired'));
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
export { emailApiClient }; 